import React, { useState, useEffect, useCallback, useRef } from 'react';
import './App.css';
import Stats from './components/Stats';
import PatternList from './components/PatternList';
import SearchBar from './components/SearchBar';
import EditModal from './components/EditModal';
import ADRLHealth from './components/ADRLHealth';
import Onboarding from './components/Onboarding';
import EvalTrends from './components/EvalTrends';
import Catalog from './components/Catalog';
import Aliases from './components/Aliases';
import AddProgram from './components/AddProgram';
import UserAccess from './components/UserAccess';
import InstitutionSelector from './components/InstitutionSelector';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

function App() {
  const [activeSection, setActiveSection] = useState(
    () => localStorage.getItem('admin_activeSection') || 'prerequisites'
  );
  const [stats, setStats] = useState(null);
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingPattern, setEditingPattern] = useState(null);
  const [error, setError] = useState(null);

  // Mirror of searchTerm for reads that must NOT retrigger the fetch effect:
  // search is submit-driven (button/Enter), so typing in the box must not
  // refetch — a searchTerm dependency on the effect would fetch per keystroke.
  const searchTermRef = useRef(searchTerm);
  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/stats`);
      if (!response.ok) throw new Error(`Failed to load stats (HTTP ${response.status})`);
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(err.message || 'Failed to load stats');
    }
  }, []);

  const fetchPatterns = useCallback(async (targetPage, search) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: targetPage,
        per_page: 20,
        ...(search && { search })
      });

      const response = await fetch(`${API_URL}/api/prerequisites/patterns?${params}`);
      if (!response.ok) throw new Error(`Failed to load prerequisite patterns (HTTP ${response.status})`);
      const data = await response.json();
      setPatterns(data.patterns);
      setTotalPages(Math.ceil(data.total / 20));
    } catch (err) {
      console.error('Error fetching patterns:', err);
      setError(err.message || 'Failed to load prerequisite patterns. Is the admin backend running?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('admin_activeSection', activeSection);
    if (activeSection === 'prerequisites') {
      fetchStats();
      fetchPatterns(page, searchTermRef.current);
    }
  }, [page, activeSection, fetchStats, fetchPatterns]);

  const handleSearch = () => {
    setPage(1);
    fetchPatterns(1, searchTerm);
  };

  const handlePatternClick = (pattern) => {
    setEditingPattern(pattern);
  };

  const handleSave = (result) => {
    fetchStats();
    fetchPatterns(page, searchTermRef.current);
    alert(`Successfully updated ${result.affected_courses} courses`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header with Navigation */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-900">
              Academic Planner — Admin Console
            </h1>
            <InstitutionSelector />
          </div>

          {/* Navigation Tabs */}
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveSection('prerequisites')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeSection === 'prerequisites'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Prerequisite Review
              </button>
              <button
                onClick={() => setActiveSection('catalog')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeSection === 'catalog'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Catalog
              </button>
              <button
                onClick={() => setActiveSection('aliases')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeSection === 'aliases'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Aliases
              </button>
              <button
                onClick={() => setActiveSection('add-program')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeSection === 'add-program'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Add Program
              </button>
              <button
                onClick={() => setActiveSection('adrl-health')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeSection === 'adrl-health'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Program Health
              </button>
              <button
                onClick={() => setActiveSection('onboarding')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeSection === 'onboarding'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Onboarding
              </button>
              <button
                onClick={() => setActiveSection('eval-trends')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeSection === 'eval-trends'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Eval Trends
              </button>
              <button
                onClick={() => setActiveSection('user-access')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeSection === 'user-access'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Users &amp; Access
              </button>
            </nav>
          </div>
        </div>

        {/* Content Sections */}
        {activeSection === 'prerequisites' && (
          <div>
            {error && (
              <div className="mb-6 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">
                <span className="font-semibold">Couldn’t load prerequisite data:</span> {error}
              </div>
            )}

            {stats && <Stats stats={stats} />}

            <div className="mt-8">
              <SearchBar 
                value={searchTerm}
                onChange={setSearchTerm}
                onSearch={handleSearch}
              />
              
              <PatternList 
                patterns={patterns} 
                loading={loading}
                onPatternClick={handlePatternClick}
              />
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex justify-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-4 py-2 bg-gray-200 rounded-md disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <span className="px-4 py-2">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-4 py-2 bg-gray-200 rounded-md disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
            
            {editingPattern && (
              <EditModal
                pattern={editingPattern}
                onClose={() => setEditingPattern(null)}
                onSave={handleSave}
              />
            )}
          </div>
        )}

        {activeSection === 'catalog' && (
          <Catalog />
        )}

        {activeSection === 'aliases' && (
          <Aliases />
        )}

        {activeSection === 'add-program' && (
          <AddProgram />
        )}

        {activeSection === 'adrl-health' && (
          <ADRLHealth />
        )}

        {activeSection === 'onboarding' && (
          <Onboarding />
        )}

        {activeSection === 'eval-trends' && (
          <EvalTrends />
        )}

        {activeSection === 'user-access' && (
          <UserAccess />
        )}

      </div>
    </div>
  );
}

export default App;
